import React, { Component } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import API from "../utils/api";
import { formatDate } from "../utils/dateFormatter";
import RecordingCell from "../components/RecordingCell";
import { PERMISSION } from "../utils/constants";
import { hasPermission } from "../utils/permissions";

const parseLocationOptions = (params) => {
    const locationValues = params.getAll("location");
    if (!locationValues.length) {
        return [];
    }

    let normalizedLocations = locationValues;

    if (locationValues.length === 1) {
        const [singleValue] = locationValues;

        if (singleValue && singleValue.trim().startsWith("[") && singleValue.trim().endsWith("]")) {
            try {
                const parsed = JSON.parse(singleValue);
                if (Array.isArray(parsed)) {
                    normalizedLocations = parsed;
                }
            } catch (e) {
                normalizedLocations = [singleValue];
            }
        }
    }

    return normalizedLocations
        .map((item) => String(item).trim())
        .filter(Boolean)
        .map((item) => ({ label: item, value: item }));
};

class AdminPostedReport extends Component {
    state = {
        leads: [],
        count: 0,
        loading: true,
        page: 0,
        pageSize: 50,
        from: "",
        to: "",
        lead_id: "",
        source: [],
        phone: "",
        customer_name: "",
        location: [],
        medicine_type: [],
        service_for: [],
        service_type: "",
        selling_purpose: "",
        user_id: "",
        team_id: "",
        users: [],
        teams: [],
        sourceOptions: [],
        campaignOptions: [],
        medicineTypeOptions: [],
        serviceForOptions: [],
        serviceTypeOptions: [],
        sellingPurposeOptions: [],
        feedbackModalOpen: false,
        feedbackLead: null,
        feedbackScore: null,
        feedbackRemark: "",
        communicationIssue: false,
        productKnowledgeIssue: false,
        verificationComplianceIssue: false,
        accuracyIssue: false,
        callEtiquetteIssue: false,
        isFlagged: false,
        submittingFeedback: false,
        audioProgress: {},
        is_flagged: "", // filter state
        viewAuditModalOpen: false,
        viewAuditLead: null,
        uploadRecordingModalOpen: false,
        uploadRecordingLead: null,
        uploadRecordingFile: null,
        uploadingRecording: false,
    };

    handleAudioProgress = (leadId, currentTime, duration) => {
        this.setState(prevState => {
            const prev = prevState.audioProgress[leadId] || { currentTime: 0, duration: 0 };
            // Record the max time listened to handle skipping backwards
            return {
                audioProgress: {
                    ...prevState.audioProgress,
                    [leadId]: {
                        currentTime: Math.max(prev.currentTime, currentTime),
                        duration: duration || prev.duration
                    }
                }
            };
        });
    };

    componentDidMount() {
        this.loadFiltersFromUrl();
        this.loadFilters();
    }

    componentDidUpdate(prevProps) {
        // Fetch report when URL changes (from navigation)
        if (prevProps.location.search !== this.props.location.search) {
            this.loadFiltersFromUrl();
        }
    }

    loadFiltersFromUrl = () => {
        const params = new URLSearchParams(this.props.location.search);
        const from = params.get("from") || "";
        const to = params.get("to") || "";
        const lead_id = params.get("lead_id") || "";
        const source_str = params.get("source") || "";
        const phone = params.get("phone") || "";
        const location = parseLocationOptions(params);
        const medicine_type_str = params.get("medicine_type") || "";
        const service_for_str = params.get("service_for") || "";
        const service_type = params.get("service_type") || "";
        const selling_purpose = params.get("selling_purpose") || "";
        const user_id = params.get("user_id") || "";
        const team_id = params.get("team_id") || "";
        const is_flagged = params.get("is_flagged") || "";

        const source = source_str
            ? source_str.split(",").map((s) => ({ label: s.trim(), value: s.trim() })).filter((s) => s.value)
            : [];

        // Convert comma-separated strings to arrays
        const medicine_type = medicine_type_str
            ? medicine_type_str.split(",").map(m => ({ label: m.trim(), value: m.trim() }))
            : [];
        const service_for = service_for_str
            ? service_for_str.split(",").map(s => ({ label: s.trim(), value: s.trim() }))
            : [];

        this.setState({
            from,
            to,
            lead_id,
            source,
            phone,
            location,
            medicine_type,
            service_for,
            service_type,
            selling_purpose,
            is_flagged,
            user_id,
            team_id,
            campaign: "",
            page: 0,
        }, () => {
            this.fetchReport();
        });
    };

    loadFilters = async () => {
        try {
            const [usersRes, constRes, teamsRes] = await Promise.all([
                API.get("/admin/users"),
                API.get("/const-data"),
                API.get("/teams"),
            ]);
            const usersData = usersRes.data.data || usersRes.data;
            const users = Array.isArray(usersData) ? usersData : [];
            const constData = constRes.data.data || constRes.data || {};
            const teamsData = teamsRes.data.data || teamsRes.data;
            const teams = Array.isArray(teamsData) ? teamsData : [];
            this.setState({
                users,
                teams,
                sourceOptions: constData.source || [],
                medicineTypeOptions: constData.medicine_type || [],
                serviceForOptions: constData.service_for || [],
                serviceTypeOptions: constData.enquiry_type || [],
                sellingPurposeOptions: constData.selling_purpose || [],
            });
        } catch (e) {
            console.error("Failed to load filter options", e);
        }
    };

    loadCampaignOptions = async (source) => {
        try {
            if (!source || source.length !== 1) {
                this.setState({ campaignOptions: [], campaign: "" });
                return;
            }
            const res = await API.get("/campaigns?source=" + encodeURIComponent(source[0].value));
            const data = res.data.data || res.data;
            this.setState({ campaignOptions: data || [] });
        } catch (e) {
            console.error("Failed to load campaign options", e);
        }
    };

    loadLocations = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];

        try {
            const res = await API.get("/cities", { params: { q: inputValue } });
            const cities = res.data.data || res.data || [];

            return cities.map((city) => ({
                value: `${city.city}, ${city.state}`,
                label: `${city.city}, ${city.state}`,
                id: city.id,
            }));
        } catch (e) {
            return [];
        }
    };

    fetchReport = async () => {
        this.setState({ loading: true });
        try {
            const {
                page,
                pageSize,
                from,
                to,
                lead_id,
                source,
                phone,
                customer_name,
                location,
                medicine_type,
                service_for,
                service_type,
                selling_purpose,
                user_id,
                team_id,
                is_flagged,
                campaign,
            } = this.state;
            const params = { limit: pageSize, offset: page * pageSize };
            const hasDirectSearch = Boolean(lead_id || phone);

            if (hasDirectSearch) {
                if (lead_id) params.lead_id = lead_id;
                if (phone) params.phone = phone;
            } else {
                if (from) params.from = from;
                if (to) params.to = to;
                if (source && source.length > 0) {
                    params.source = source.map((s) => s.value).join(",");
                }
                if (location && location.length > 0) {
                    params.location = JSON.stringify(location.map((item) => item.value));
                }
                if (medicine_type && medicine_type.length > 0) {
                    params.medicine_type = medicine_type.map(m => m.value).join(",");
                }
                if (service_for && service_for.length > 0) {
                    params.service_for = service_for.map(s => s.value).join(",");
                }
                if (service_type) params.service_type = service_type;
                if (selling_purpose) params.selling_purpose = selling_purpose;
                if (customer_name) params.customer_name = customer_name;
                if (user_id) params.user_id = user_id;
                if (team_id) params.team_id = team_id;
                if (is_flagged) params.is_flagged = is_flagged;
                if (campaign) params.campaign = campaign;
            }

            const res = await API.get("/admin/report/posted", { params });
            const data = res.data.data || res.data;
            this.setState({
                leads: data.leads || [],
                count: data.count || 0,
                loading: false,
            });
        } catch (e) {
            toast.error("Failed to load report");
            this.setState({ loading: false });
        }
    };

    handleSearch = (e) => {
        e.preventDefault();
        this.setState({ page: 0 }, this.fetchReport);
    };

    handleReset = () => {
        this.setState(
            {
                from: "",
                to: "",
                lead_id: "",
                source: [],
                phone: "",
                customer_name: "",
                location: [],
                medicine_type: [],
                service_for: [],
                service_type: "",
                selling_purpose: "",
                user_id: "",
                team_id: "",
                campaign: "",
                page: 0,
            },
            this.fetchReport
        );
    };

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, this.fetchReport);
    };

    // Auto-posted = system posted the lead (status 6) with no tele executive attached.
    isAutoPosted = (lead) => {
        if (String(lead.status) !== "6") return false;
        return lead.tele_user_id === 0 || lead.tele_user_id === "0"
            || lead.tele_user_id === null || lead.tele_user_id === undefined;
    };

    hasRecording = (recordingUrls) => {
        let urls = [];
        if (recordingUrls) {
            try {
                urls = typeof recordingUrls === "string" ? JSON.parse(recordingUrls) : recordingUrls;
            } catch (_) {
                urls = [];
            }
        }
        return urls && urls.length > 0;
    };

    openViewAuditModal = (lead) => {
        this.setState({
            viewAuditModalOpen: true,
            viewAuditLead: lead,
        });
    };

    closeViewAuditModal = () => {
        this.setState({
            viewAuditModalOpen: false,
            viewAuditLead: null,
        });
    };

    openFeedbackModal = (lead) => {
        this.setState({
            feedbackModalOpen: true,
            feedbackLead: lead,
            feedbackScore: null,
            feedbackRemark: "",
            communicationIssue: false,
            productKnowledgeIssue: false,
            verificationComplianceIssue: false,
            accuracyIssue: false,
            callEtiquetteIssue: false,
            isFlagged: false,
        });
    };

    closeFeedbackModal = () => {
        this.setState({
            feedbackModalOpen: false,
            feedbackLead: null,
            feedbackScore: null,
            feedbackRemark: "",
        });
    };

    submitFeedback = async () => {
        const { feedbackLead, feedbackScore, feedbackRemark, audioProgress,
            communicationIssue, productKnowledgeIssue, verificationComplianceIssue,
            accuracyIssue, callEtiquetteIssue, isFlagged } = this.state;
        if (!feedbackLead) return;

        if (feedbackScore === null) {
            toast.error("Please select a score.");
            return;
        }

        if (isFlagged && !feedbackRemark.trim()) {
            toast.error("Remarks are mandatory when flagging a user.");
            return;
        }

        let listenPercentage = 0;
        let total_duration = 0;
        const progress = audioProgress[feedbackLead.id];
        if (progress && progress.duration > 0) {
            listenPercentage = (progress.currentTime / progress.duration) * 100;
            listenPercentage = Math.min(100, parseFloat(listenPercentage.toFixed(2)));
            total_duration = Math.round(progress.duration);
        }

        this.setState({ submittingFeedback: true });
        try {
            await API.post("/admin/report/feedback", {
                lead_id: feedbackLead.id,
                tele_user_id: feedbackLead.tele_user_id,
                lead_allocation_id: feedbackLead.allocation_id,
                score: feedbackScore,
                remark: feedbackRemark,
                listen_duration_percentage: listenPercentage,
                total_duration,
                communication_issue: communicationIssue,
                product_knowledge_issue: productKnowledgeIssue,
                verification_compliance_issue: verificationComplianceIssue,
                accuracy_issue: accuracyIssue,
                call_etiquette_issue: callEtiquetteIssue,
                is_flagged: isFlagged
            });
            toast.success("Feedback submitted successfully");
            this.setState({ feedbackModalOpen: false, feedbackLead: null });

            // update lead in state to show as audited
            this.setState(prev => ({
                leads: prev.leads.map(l => l.id === feedbackLead.id ? { ...l, is_audited: true } : l)
            }));
        } catch (e) {
            toast.error(e.response?.data?.message || "Failed to submit feedback");
        }
        this.setState({ submittingFeedback: false });
    };

    openUploadRecordingModal = (lead) => {
        this.setState({
            uploadRecordingModalOpen: true,
            uploadRecordingLead: lead,
            uploadRecordingFile: null,
        });
    };

    closeUploadRecordingModal = () => {
        this.setState({
            uploadRecordingModalOpen: false,
            uploadRecordingLead: null,
            uploadRecordingFile: null,
        });
    };

    handleUploadRecordingFileChange = (e) => {
        this.setState({ uploadRecordingFile: e.target.files[0] || null });
    };

    submitUploadRecording = async () => {
        const { uploadRecordingLead, uploadRecordingFile } = this.state;
        if (!uploadRecordingLead || !uploadRecordingFile) {
            toast.error("Please select a recording file to upload.");
            return;
        }

        const formData = new FormData();
        formData.append("recording", uploadRecordingFile);
        formData.append("lead_id", uploadRecordingLead.id);
        formData.append("allocation_id", uploadRecordingLead.allocation_id);

        this.setState({ uploadingRecording: true });
        try {
            await API.put("/admin/recording/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Recording uploaded successfully");
            this.setState({ uploadRecordingModalOpen: false, uploadRecordingLead: null, uploadRecordingFile: null });
            this.fetchReport();
        } catch (e) {
            toast.error(e.response?.data?.message || "Failed to upload recording");
        }
        this.setState({ uploadingRecording: false });
    };

    handleDeleteLead = async (leadId) => {
        if (!window.confirm("Are you sure you want to delete this lead?")) return;
        try {
            await API.post(`/lead/delete/${leadId}`);
            toast.success("Lead deleted successfully");
            this.fetchReport();
        } catch (e) {
            toast.error("Failed to delete lead");
        }
    };

    render() {
        const {
            leads, count, loading, page, pageSize, from, to, lead_id, source, phone, customer_name, location,
            medicine_type, service_for, service_type, selling_purpose, user_id, team_id, campaign,
            users, teams, sourceOptions, campaignOptions, medicineTypeOptions, serviceForOptions, serviceTypeOptions, sellingPurposeOptions,
        } = this.state;
        const totalPages = Math.ceil(count / pageSize);
        const isDirectSearch = Boolean(lead_id || phone);

        return (
            <div>
                <h2 className="page-header">
                    <i className="fas fa-chart-bar"></i> Posted Leads Report
                </h2>

                {/* Filters */}
                <form onSubmit={this.handleSearch} className="well" style={{ marginBottom: 20, padding: 15 }}>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>From</label>
                                <input type="date" className="form-control" value={from}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ from: e.target.value })} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>To</label>
                                <input type="date" className="form-control" value={to}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ to: e.target.value })} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Source</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    isDisabled={isDirectSearch}
                                    options={sourceOptions.map((s) => ({ label: s, value: s }))}
                                    value={source}
                                    onChange={(selected) => {
                                        this.setState({ source: selected || [], campaign: "" });
                                        this.loadCampaignOptions(selected || []);
                                    }}
                                    placeholder="All sources"
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Campaign</label>
                                <select className="form-control" value={campaign || ""}
                                    disabled={isDirectSearch || source.length !== 1}
                                    onChange={(e) => this.setState({ campaign: e.target.value })}>
                                    <option value="">Campaign</option>
                                    {campaignOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Service Type</label>
                                <select className="form-control" value={service_type}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ service_type: e.target.value })}>
                                    <option value="">All Services</option>
                                    {serviceTypeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Medicine Type</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    isDisabled={isDirectSearch}
                                    options={medicineTypeOptions.map((m) => ({ label: m, value: m }))}
                                    value={medicine_type}
                                    onChange={(selected) => this.setState({ medicine_type: selected || [] })}
                                    placeholder="All Types"
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Service For</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    isDisabled={isDirectSearch}
                                    options={serviceForOptions.map((s) => ({ label: s, value: s }))}
                                    value={service_for}
                                    onChange={(selected) => this.setState({ service_for: selected || [] })}
                                    placeholder="All Service For"
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Selling Purpose</label>
                                <select className="form-control" value={selling_purpose}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ selling_purpose: e.target.value })}>
                                    <option value="">All Purposes</option>
                                    {sellingPurposeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Location</label>
                                <AsyncSelect
                                    isMulti
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={this.loadLocations}
                                    value={location}
                                    isDisabled={isDirectSearch}
                                    onChange={(selected) => this.setState({ location: selected || [] })}
                                    placeholder="Search location..."
                                    noOptionsMessage={() => "Type to search..."}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Executive</label>
                                <select className="form-control" value={user_id}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ user_id: e.target.value })}>
                                    <option value="">All Executives</option>
                                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Team</label>
                                <select className="form-control" value={team_id}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ team_id: e.target.value })}>
                                    <option value="">All Teams</option>
                                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Flagged Status</label>
                                <select className="form-control" value={this.state.is_flagged}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ is_flagged: e.target.value })}>
                                    <option value="">All</option>
                                    <option value="1">Flagged</option>
                                    <option value="0">Not Flagged</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">

                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Direct Lead ID Search</label>
                                <input type="text" className="form-control" placeholder="Lead ID"
                                    value={lead_id}
                                    onChange={(e) => {
                                        this.setState({ lead_id: e.target.value, phone: "" });
                                    }} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Direct Phone Search</label>
                                <input type="text" className="form-control" placeholder="Phone starts with..."
                                    value={phone}
                                    onChange={(e) => {
                                        this.setState({ phone: e.target.value, lead_id: "" });
                                    }} />
                            </div>
                        </div>

                        
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Customer Name(Exact Name)</label>
                                <input type="text" className="form-control" placeholder="Exact name..."
                                    value={customer_name}
                                    disabled={isDirectSearch}
                                    onChange={(e) => this.setState({ customer_name: e.target.value })} />
                            </div>
                        </div>

                        <div className="col-md-3">
                            <div className="form-group">
                                <label>&nbsp;</label>
                                <div>
                                    <button type="submit" className="btn btn-primary" style={{ marginRight: 5 }}>
                                        <i className="fas fa-search"></i> Search
                                    </button>
                                    <button type="button" className="btn btn-default" onClick={this.handleReset}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>



                    </div>
                </form>

                {/* Table */}
                {loading ? (
                    <div className="text-center"><i className="fas fa-spinner fa-spin fa-2x"></i></div>
                ) : (
                    <div>
                        <p>Total Leads: <strong>{count}</strong></p>
                        <style>{`tr.auto-posted-row > td { background-color: #fff3cd !important; }`}</style>
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th style={{ minWidth: 200 }}>Message</th>
                                        <th>Customer</th>
                                        <th>Mobile</th>
                                        <th>Email</th>
                                        <th>Address</th>
                                        <th>Source</th>
                                        <th>Service Type</th>
                                        <th>Medicine Type</th>
                                        <th>Service For</th>
                                        <th>GST Number</th>
                                        <th>Drug License</th>
                                        <th>FSSAI License</th>
                                        <th>Executive</th>
                                        <th>Remark</th>
                                        <th>Incentive Plan</th>
                                        <th>Processed At</th>
                                        {hasPermission(this.props.permissions, PERMISSION.REPORT_POSTED_VIEW_BUY_LEAD_PCT) && <th>Buy Lead %</th>}
                                        <th>Recordings</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.length === 0 ? (
                                        <tr><td colSpan="16" className="text-center">No leads found</td></tr>
                                    ) : (
                                        leads.map((lead) => (
                                            <tr key={lead.id} className={this.isAutoPosted(lead) ? "auto-posted-row" : ""}>
                                                <td>
                                                    {lead.id}
                                                    {this.isAutoPosted(lead) && (
                                                        <span className="label label-warning" style={{ marginLeft: 5 }} title="Auto-posted lead (no tele executive)">
                                                            <i className="fa fa-bolt"></i> Auto
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{lead.message || "-"}</td>
                                                <td>{lead.name}</td>
                                                <td>{lead.mobile}</td>
                                                <td>{lead.email}</td>
                                                <td>{lead.address || "-"}</td>
                                                <td>{lead.source}</td>
                                                <td>{lead.service_type}</td>
                                                <td>{lead.medicine_type}</td>
                                                <td>{[lead.primary_service_for, lead.service_for].filter(Boolean).join(", ") || "-"}</td>
                                                <td>{lead.gst_number == 1 ? "Yes" : (lead.gst_number == 2 ? "Planning": "No")}</td>
                                                <td>{lead.drug_license == 1 ? "Yes" : (lead.drug_license == 2 ? "Planning" : "No")}</td>
                                                <td>{lead.fssai_license == 1 ? "Yes" : (lead.fssai_license == 2 ? "Planning" : "No")}</td>
                                                <td>{lead.executive_name}</td>
                                                <td>{lead.remark || "-"}</td>
                                                <td>{lead.incentive_plan || "-"}</td>
                                                <td>{formatDate(lead.processed_at || lead.posted_on)}</td>
                                                {hasPermission(this.props.permissions, PERMISSION.REPORT_POSTED_VIEW_BUY_LEAD_PCT) && <td>{lead.buy_count != null ? Math.max(0, Math.round(100 - (lead.buy_count / 12) * 100)) + '%' : '100%'}</td>}
                                                <td>
                                                    <RecordingCell
                                                        leadId={lead.id}
                                                        allocationId={lead.allocation_id}
                                                        recordingUrls={lead.recording_urls}
                                                        onAudioProgress={this.handleAudioProgress}
                                                    />
                                                </td>
                                                <td>
                                                    {hasPermission(this.props.permissions, PERMISSION.REPORT_POSTED_DELETE_LEAD) && (
                                                        <button className="btn btn-xs btn-danger" onClick={() => this.handleDeleteLead(lead.id)} title="Delete Lead">
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    )}
                                                    {hasPermission(this.props.permissions, PERMISSION.REPORT_POSTED_UPLOAD_RECORDING) && (
                                                        <button className="btn btn-xs btn-warning ml-1" onClick={() => this.openUploadRecordingModal(lead)} title="Upload Recording">
                                                            <i className="fa fa-upload"></i>
                                                        </button>
                                                    )}
                                                    {this.hasRecording(lead.recording_urls) && (
                                                        lead.is_audited ? (
                                                            <button className="btn btn-xs btn-success ml-1" onClick={() => (lead.audit_details ? this.openViewAuditModal(lead) : toast.error("Audit details not available for this lead"))} title="View Audit Report">
                                                                <i className="fa fa-eye"></i> View Audit
                                                            </button>
                                                        ) : (
                                                            <button className="btn btn-xs btn-info ml-1" onClick={() => this.openFeedbackModal(lead)} title="Call Audit Feedback">
                                                                <i className="fa fa-comment"></i>
                                                            </button>
                                                        )
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <nav className="text-center">
                                <ul className="pagination">
                                    <li className={page === 0 ? "disabled" : ""}>
                                        <a href="#prev" onClick={(e) => { e.preventDefault(); if (page > 0) this.handlePageChange(page - 1); }}>&laquo;</a>
                                    </li>
                                    {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                                        let p = totalPages <= 10 ? i : Math.max(0, Math.min(page - 4, totalPages - 10)) + i;
                                        return (
                                            <li key={p} className={page === p ? "active" : ""}>
                                                <a href={`#page-${p}`} onClick={(e) => { e.preventDefault(); this.handlePageChange(p); }}>{p + 1}</a>
                                            </li>
                                        );
                                    })}
                                    <li className={page >= totalPages - 1 ? "disabled" : ""}>
                                        <a href="#next" onClick={(e) => { e.preventDefault(); if (page < totalPages - 1) this.handlePageChange(page + 1); }}>&raquo;</a>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                )}
                {this.state.feedbackModalOpen && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Call Audit Feedback</h5>
                                    <button type="button" className="close" onClick={this.closeFeedbackModal}>&times;</button>
                                </div>
                                <div className="modal-body">

                                    <div className="form-group mt-3">
                                        <label className="mb-2">Checklist &nbsp;<small className="ms-2">(Choose those where there is an issue)</small></label>
                                        <div className="row align-items-stretch">
                                            {[
                                                { stateKey: 'communicationIssue', label: 'Communication Skills', subtext: 'Clear speech, confidence, politeness, active listening.' },
                                                { stateKey: 'productKnowledgeIssue', label: 'Product & Process Knowledge', subtext: 'Technical knowledge and process knowledge.' },
                                                { stateKey: 'verificationComplianceIssue', label: 'Verification Checklist Compliance', subtext: 'Followed call check list.' },
                                                { stateKey: 'accuracyIssue', label: 'Accuracy of Information', subtext: 'Inputted proper information as per the call.' },
                                                { stateKey: 'callEtiquetteIssue', label: 'Call Etiquette', subtext: 'Professionalism and service orientation.' }
                                            ].map((item, idx) => {
                                                const hasIssue = this.state[item.stateKey];
                                                return (
                                                    <div className="col-sm-6 mb-3" key={idx}>
                                                        <button
                                                            type="button"
                                                            className={`list-group-item list-group-item-action ${hasIssue ? 'list-group-item-danger' : ''}`}
                                                            onClick={() => this.setState({ [item.stateKey]: !hasIssue })}
                                                            style={{ padding: '8px 12px', height: '100%', borderRadius: '4px', border: '1px solid rgba(0,0,0,.125)', marginBottom: '8px' }}
                                                        >
                                                            <div className="d-flex w-100 justify-content-between">
                                                                <h6 className="mb-1 text-left" style={{ fontWeight: 'bold' }}>{item.label}</h6>
                                                            </div>
                                                            <small className="text-left" style={{ display: 'block', lineHeight: '1.2' }}>{item.subtext}</small>
                                                        </button>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>

                                    <div className="form-group mt-3 form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input mt-1"
                                            id="flagUser"
                                            checked={this.state.isFlagged}
                                            onChange={(e) => this.setState({ isFlagged: e.target.checked })}
                                        />
                                        <label className="form-check-label text-danger font-weight-bold ml-2" htmlFor="flagUser" style={{ cursor: 'pointer' }}>
                                            &nbsp;Flag User (Impacts performance rating)
                                        </label>
                                    </div>

                                    <div className="form-group mt-3">
                                        <label>Remarks {this.state.isFlagged && <span className="text-danger">*</span>}</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={this.state.feedbackRemark}
                                            onChange={(e) => this.setState({ feedbackRemark: e.target.value })}
                                            placeholder="Enter remarks here..."
                                        ></textarea>
                                    </div>


                                    <div className="form-group">
                                        <label>Score {this.state.feedbackScore !== null ? `(${this.state.feedbackScore})` : ''}</label>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(s => {
                                                const color = s <= 4 ? '#d9534f' : s <= 7 ? '#f0ad4e' : '#5cb85c';
                                                const isSelected = this.state.feedbackScore === s;
                                                return (
                                                    <button
                                                        key={s}
                                                        type="button"
                                                        onClick={() => this.setState({ feedbackScore: s })}
                                                        style={{
                                                            width: '32px', height: '32px',
                                                            backgroundColor: isSelected ? color : 'transparent',
                                                            color: isSelected ? 'white' : color,
                                                            border: `2px solid ${color}`,
                                                            borderRadius: '4px',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontWeight: 'bold', cursor: 'pointer'
                                                        }}
                                                    >
                                                        {s}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.closeFeedbackModal}>Cancel</button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={this.submitFeedback}
                                        disabled={this.state.submittingFeedback}
                                    >
                                        {this.state.submittingFeedback ? "Submitting..." : "Submit Feedback"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {this.state.viewAuditModalOpen && this.state.viewAuditLead && this.state.viewAuditLead.audit_details && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Audit Report: Lead #{this.state.viewAuditLead.id}</h5>
                                    <button type="button" className="close" onClick={this.closeViewAuditModal}>&times;</button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <strong>Audit Date:</strong> {new Date(this.state.viewAuditLead.audit_details.audit_date).toLocaleString()}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Score:</strong> {this.state.viewAuditLead.audit_details.score} / 10
                                    </div>
                                    {this.state.viewAuditLead.audit_details.is_flagged === 1 && (
                                        <div className="alert alert-danger p-2 mb-3">
                                            <strong><i className="fa fa-flag"></i> Flagged Lead</strong>
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <strong>Remarks:</strong>
                                        <div className="p-2 bg-light mt-1" style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
                                            {this.state.viewAuditLead.audit_details.remark || "No remarks provided."}
                                        </div>
                                    </div>
                                    <div>
                                        <strong>Issues Identified:</strong>
                                        <ul className="list-group mt-2">
                                            {this.state.viewAuditLead.audit_details.communication_issue === 1 && <li className="list-group-item text-danger py-1">Communication Skills</li>}
                                            {this.state.viewAuditLead.audit_details.product_knowledge_issue === 1 && <li className="list-group-item text-danger py-1">Product & Process Knowledge</li>}
                                            {this.state.viewAuditLead.audit_details.verification_compliance_issue === 1 && <li className="list-group-item text-danger py-1">Verification Checklist Compliance</li>}
                                            {this.state.viewAuditLead.audit_details.accuracy_issue === 1 && <li className="list-group-item text-danger py-1">Accuracy of Information</li>}
                                            {this.state.viewAuditLead.audit_details.call_etiquette_issue === 1 && <li className="list-group-item text-danger py-1">Call Etiquette</li>}
                                            {!(this.state.viewAuditLead.audit_details.communication_issue === 1 || this.state.viewAuditLead.audit_details.product_knowledge_issue === 1 || this.state.viewAuditLead.audit_details.verification_compliance_issue === 1 || this.state.viewAuditLead.audit_details.accuracy_issue === 1 || this.state.viewAuditLead.audit_details.call_etiquette_issue === 1) && (
                                                <li className="list-group-item text-success py-1">No issues identified in checklist.</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.closeViewAuditModal}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {this.state.uploadRecordingModalOpen && this.state.uploadRecordingLead && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Upload Call Recording</h5>
                                    <button type="button" className="close" onClick={this.closeUploadRecordingModal}>&times;</button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <strong>Lead:</strong> #{this.state.uploadRecordingLead.id} — {this.state.uploadRecordingLead.name}
                                    </div>
                                    <div className="form-group">
                                        <label className="mb-2">Recording File</label>
                                        <input
                                            type="file"
                                            accept="audio/*"
                                            className="form-control"
                                            onChange={this.handleUploadRecordingFileChange}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.closeUploadRecordingModal}>Cancel</button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={this.submitUploadRecording}
                                        disabled={this.state.uploadingRecording || !this.state.uploadRecordingFile}
                                    >
                                        {this.state.uploadingRecording ? "Uploading..." : "Upload"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({ permissions: state.permissions });

export default connect(mapStateToProps)(AdminPostedReport);


